import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatDistanceToNow } from 'date-fns';
import ConfirmationModal from '../../components/UI/ConfirmationModal';
import { useTranslation } from 'react-i18next';

const AdminPendingReview = () => {
  const { t } = useTranslation();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const [releaseModal, setReleaseModal] = useState({ isOpen: false, user: null });
  
  
  useEffect(() => {
    fetchPendingUsers();
  }, []);
  
  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users/pending-review');
      setPendingUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const impersonateUser = async (userId) => {
    setImpersonating(userId);
    try {
      const response = await api.post(`/admin/impersonate/${userId}`);
      
      // Store original token
      localStorage.setItem('originalToken', localStorage.getItem('accessToken'));
      localStorage.setItem('impersonationToken', response.data.token);
      localStorage.setItem('accessToken', response.data.token);
      
      // Redirect to search page for query review
      window.location.href = '/search';
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      setImpersonating(null);
    }
  };
  
  const handleReleaseClick = (user) => {
    setReleaseModal({
      isOpen: true,
      user: user
    });
  };
  
  const handleReleaseConfirm = async () => {
    if (!releaseModal.user) return;
    
    const userId = releaseModal.user.id;
    setReleasing(userId);
    
    try {
      await api.post(`/admin/users/${userId}/release-dashboard`, {
        sendNotification: true
      });
      
      // Remove user from pending list
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      
      // Close modal
      setReleaseModal({ isOpen: false, user: null });
      
      // Show success (you might want to add a toast notification here)
      console.log('Dashboard released successfully');
    } catch (error) {
      console.error('Failed to release dashboard:', error);
      alert(error.response?.data?.error || 'Failed to release dashboard');
    } finally {
      setReleasing(null);
    }
  };
  
  const handleReleaseCancel = () => {
    setReleaseModal({ isOpen: false, user: null });
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8">Loading...</div>
      </div>
    );
  }
  
  if (pendingUsers.length === 0) {
    return (
      <div className="text-center py-12">
        <div className={`text-mono text-gray-600 dark:text-[#c4c4c4]`}>
          <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{t('admin.noPendingUsers')}</p>
          <p className="text-sm mt-2">{t('admin.allUsersReviewed')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="mb-6">
        <h2 className={`text-mono text-xl font-semibold mb-2 text-gray-800 dark:text-[#f5f5f5]`}>
          Pending Dashboard Reviews
        </h2>
        <p className={`text-mono text-sm text-gray-600 dark:text-[#c4c4c4]`}>
          {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} awaiting dashboard release
        </p>
      </div>
      
      <div className="space-y-4">
        {pendingUsers.map(user => (
          <div key={user.id} className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* User Info */}
              <div className="flex-1">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-500 text-white w-10 h-10 rounded-full flex items-center justify-center text-white font-bold">
                    {user.email[0].toUpperCase()}
                  </div>
                  <div>
                    <h3 className={`text-mono font-semibold text-gray-800 dark:text-[#f5f5f5]`}>
                      {user.business_name || user.email}
                    </h3>
                    <p className={`text-mono text-sm text-gray-600 dark:text-[#c4c4c4]`}>
                      {user.email}
                    </p>
                    {user.business_website && (
                      <a 
                        href={user.business_website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-mono text-xs text-accent-400 hover:text-accent-300"
                      >
                        {user.business_website}
                      </a>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex gap-6">
                <div className="text-center">
                  <p className={`text-mono text-xs text-gray-600 dark:text-[#c4c4c4]`}>
                    Query Templates
                  </p>
                  <p className={`text-mono font-semibold text-gray-800 dark:text-[#f5f5f5]`}>
                    {user.query_template_count || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className={`text-mono text-xs text-gray-600 dark:text-[#c4c4c4]`}>
                    Executions
                  </p>
                  <p className={`text-mono font-semibold text-gray-800 dark:text-[#f5f5f5]`}>
                    {user.execution_batch_count || 0}
                  </p>
                </div>
              </div>
              
              {/* Waiting Time */}
              <div className="text-center">
                <p className={`text-mono text-xs text-gray-600 dark:text-[#c4c4c4]`}>
                  Waiting
                </p>
                <p className={`text-mono font-semibold ${
                  user.waitingTime > 120 ? 'text-red-400' : 
                  user.waitingTime > 60 ? 'text-yellow-400' : 
                  'text-gray-800 dark:text-[#f5f5f5]'
                }`}>
                  {user.onboarding_completed_at 
                    ? formatDistanceToNow(new Date(user.onboarding_completed_at), { addSuffix: false })
                    : 'Unknown'}
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => impersonateUser(user.id)}
                  disabled={impersonating === user.id}
                  className="bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#262626] px-4 py-2 text-sm hover:scale-105 transform transition-all disabled:opacity-50"
                >
                  {impersonating === user.id ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4">...</div>
                      Impersonating...
                    </span>
                  ) : (
                    'Review & Edit'
                  )}
                </button>
                
                <button
                  onClick={() => handleReleaseClick(user)}
                  disabled={releasing === user.id}
                  className="bg-blue-500 text-white hover:bg-blue-600 px-4 py-2 text-sm hover:scale-105 transform transition-all disabled:opacity-50"
                >
                  {releasing === user.id ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4">...</div>
                      Releasing...
                    </span>
                  ) : (
                    'Release Dashboard'
                  )}
                </button>
              </div>
            </div>
            
            {/* Onboarding Time */}
            <div className={`mt-4 pt-4 border-t border-black/10`}>
              <p className={`text-mono text-xs text-gray-500 dark:text-[#999999]`}>
                Onboarding completed: {new Date(user.onboarding_completed_at).toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>
      
      {/* Release Confirmation Modal */}
      <ConfirmationModal
        isOpen={releaseModal.isOpen}
        onClose={handleReleaseCancel}
        onConfirm={handleReleaseConfirm}
        title="Release Dashboard"
        message={`Are you sure you want to release the dashboard for ${releaseModal.user?.business_name || releaseModal.user?.email}? An email notification will be sent to the user.`}
        confirmText="Release Dashboard"
        cancelText="Cancel"
        isDestructive={false}
      />
    </div>
  );
};

export default AdminPendingReview;